import { z } from 'zod';
import { LEGAL_DOCUMENT_IDS } from './legal-document-paths';

/**
 * The shape of a fixed legal document's content file. The text is a legal
 * document rather than UI copy, so it ships as data in the app that serves it
 * instead of as catalog keys (ADR-017 Rule 19).
 */

const linkRunSchema = z.object({ text: z.string().min(1), href: z.string().min(1) });
const lineBreakRunSchema = z.object({ lineBreak: z.literal(true) });
const textRunSchema = z.object({ text: z.string().min(1) });

/** Link first: a plain-text object schema would otherwise accept a link and drop its href. */
export const legalRunSchema = z.union([linkRunSchema, lineBreakRunSchema, textRunSchema]);

const runsSchema = z.array(legalRunSchema).min(1);

const paragraphBlockSchema = z.object({ type: z.literal('paragraph'), runs: runsSchema });
const listBlockSchema = z.object({ type: z.literal('list'), items: z.array(runsSchema).min(1) });

/** A table cell holds paragraphs and lists, as the legacy markup does. */
const tableCellSchema = z.array(
  z.discriminatedUnion('type', [paragraphBlockSchema, listBlockSchema]),
);

const headingBlockSchema = z.object({
  type: z.literal('heading'),
  level: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  text: z.string().min(1),
  anchorId: z.string().min(1).optional(),
});

/** A table with no header row is a layout table, such as a table of contents. */
const tableBlockSchema = z.object({
  type: z.literal('table'),
  header: z.array(tableCellSchema).min(1).optional(),
  rows: z.array(z.array(tableCellSchema)).min(1),
});

export const legalBlockSchema = z.discriminatedUnion('type', [
  headingBlockSchema,
  paragraphBlockSchema,
  listBlockSchema,
  tableBlockSchema,
]);

export const legalDocumentSchema = z.object({
  id: z.enum(LEGAL_DOCUMENT_IDS),
  title: z.string().min(1),
  blocks: z.array(legalBlockSchema).min(1),
});

export type LegalRun = z.infer<typeof legalRunSchema>;
export type LegalBlock = z.infer<typeof legalBlockSchema>;
export type LegalTableCell = z.infer<typeof tableCellSchema>;
export type LegalDocument = z.infer<typeof legalDocumentSchema>;
